import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "../components/molecules/DashboardTable/DashboardTable.scss";
import { DashboardLayout } from "../components/organisms/Layout";
import DashboardApi from "../api/Dashboard";
import Category from "../api/Category";
import { useNavigate } from "react-router-dom";
import { formatPhone } from "../utils";
import { defaultUserImage } from "../assets/images";
import DashboardStatsList from "../components/molecules/DashboardStatsList/DashboardStatsList";

const Dashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loadingStates, setLoadingStates] = useState({
    recentLogins: true,
    recentLocations: true,
    recentSeekers: true,
    recentApplications: true,
    categories: true,
  });
  const [recentLogins, setRecentLogins] = useState([]);
  const [recentLocations, setRecentLocations] = useState([]);
  const [recentSeekers, setRecentSeekers] = useState([]);
  const [recentApplications, setRecentApplications] = useState([]);
  const [categories, setCategories] = useState([]);

  const loadCategories = () => {
    Category.getAll()
      .then((response) => {
        setCategories(response.data.data);
      })
      .catch((err) => {
        console.error("Error loading categories:", err);
      })
      .finally(() =>
        setLoadingStates((prev) => ({ ...prev, categories: false }))
      );
  };

  const loadRecentLogins = async () => {
    try {
      const response = await DashboardApi.getRecentLogins();
      if (response?.data?.data) {
        setRecentLogins(response.data.data);
      }
    } catch (err) {
      console.error("Error loading recent logins:", err);
    } finally {
      setLoadingStates((prev) => ({ ...prev, recentLogins: false }));
    }
  };

  const loadRecentLocations = async () => {
    try {
      const response = await DashboardApi.getRecentLocations();
      if (response?.data?.data) {
        setRecentLocations(response.data.data);
      }
    } catch (err) {
      console.error("Error loading recent locations:", err);
    } finally {
      setLoadingStates((prev) => ({ ...prev, recentLocations: false }));
    }
  };

  const loadRecentSeekers = async () => {
    try {
      const response = await DashboardApi.getRecentSeekers();
      if (response?.data?.data) {
        setRecentSeekers(response.data.data);
      }
    } catch (err) {
      console.error("Error loading recent seekers:", err);
    } finally {
      setLoadingStates((prev) => ({ ...prev, recentSeekers: false }));
    }
  };

  const loadRecentApplications = async () => {
    try {
      const response = await DashboardApi.getRecentApplications();
      if (response?.data?.data) {
        setRecentApplications(response.data.data);
      }
    } catch (err) {
      console.error("Error loading recent applications:", err);
    } finally {
      setLoadingStates((prev) => ({ ...prev, recentApplications: false }));
    }
  };

  // Set document title
  useEffect(() => {
    document.title = "HeyHire Portal-Dashboard";
  }, []);

  // Load all dashboard data
  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        await Promise.all([
          loadRecentLogins(),
          loadRecentLocations(),
          loadRecentSeekers(),
          loadRecentApplications(),
          loadCategories(),
        ]);
      } catch (error) {
        console.error("Error loading dashboard data:", error);
      }
    };

    loadDashboardData();
  }, []);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="mb-8">
          <h1 className="text-2xl font-bold tracking-tight text-gray-900">
            {t("dashboard.your-dashboard")}
          </h1>
          <p className="mt-2 text-sm sm:text-base text-gray-600">
            Overview of recent activity and key metrics
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Logins */}
          <div className="lg:col-span-1 h-full">
            <DashboardStatsList
              title={t("dashboard.recent-logins")}
              items={recentLogins?.slice(0, 5)}
              loading={loadingStates.recentLogins}
              emptyMessage="No users yet"
              renderItem={(item) => (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <img
                      className="h-9 w-9 rounded-full object-cover ring-1 ring-gray-100"
                      src={item?.photo?.thumb_url || defaultUserImage}
                      alt=""
                    />
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {item?.first_name} {item?.last_name}
                      </div>
                      <div className="text-xs text-gray-500">
                        {item?.location?.name || "-"}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] text-gray-400 uppercase tracking-wider">Last Login</div>
                    <div className="text-xs font-medium text-gray-700">{item?.last_login || "Never"}</div>
                  </div>
                </div>
              )}
            />
          </div>

          {/* Recent Locations */}
          <div className="lg:col-span-1 h-full">
            <DashboardStatsList
              title={t("dashboard.latest-business")}
              items={recentLocations?.slice(0, 5)}
              loading={loadingStates.recentLocations || loadingStates.categories}
              emptyMessage="No business yet"
              renderItem={(item) => {
                let _category;
                if (Array.isArray(item?.category) && item?.category.length > 0) {
                  _category = item.category[0];
                } else if (Array.isArray(item?.category_id) && item?.category_id.length > 0) {
                  _category = categories?.find((c) => c?.id === item?.category_id[0]);
                } else {
                  _category = categories?.find((c) => c?.id === item?.category_id);
                }

                return (
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900">
                          {item?.company?.name || "-"}
                        </span>
                      </div>
                      <div className="text-xs text-primary-600 font-medium">
                        {_category?.name || "Uncategorized"}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-right">
                      <div>
                        <div className="text-[10px] text-gray-400 uppercase tracking-wider">Owner</div>
                        <div className="text-xs font-medium text-gray-700">{item?.user.length > 0
                          ? `${item?.user[0]["first_name"]} ${item?.user[0]["last_name"]}`
                          : "-"}</div>
                      </div>
                      {item?.user.length > 0 && (
                        <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center text-xs text-gray-500 font-medium">
                          {item?.user[0]["first_name"][0]}
                        </div>
                      )}
                    </div>
                  </div>
                );
              }}
            />
          </div>

          {/* Recent Seekers */}
          <div className="lg:col-span-1 h-full">
            <DashboardStatsList
              title={t("dashboard.latest-job-seekers")}
              items={recentSeekers?.slice(0, 5)}
              loading={loadingStates.recentSeekers}
              emptyMessage="No seekers yet"
              renderItem={(item) => (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <img
                      className="h-9 w-9 rounded-full object-cover ring-1 ring-gray-100"
                      src={item?.photo?.thumb_url || defaultUserImage}
                      alt=""
                    />
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {item?.first_name} {item?.last_name}
                      </div>
                      <div className="text-xs text-gray-500">
                        {formatPhone(item?.phone)}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] text-gray-400 uppercase tracking-wider">Location</div>
                    <div className="text-xs font-medium text-gray-700">{item?.city || "-"}, {item?.state || "-"}</div>
                  </div>
                </div>
              )}
            />
          </div>

          {/* Recent Applications */}
          <div className="lg:col-span-1 h-full">
            <DashboardStatsList
              title={t("dashboard.latest-application-submissions")}
              items={recentApplications?.slice(0, 5)}
              loading={loadingStates.recentApplications}
              emptyMessage="No applicants yet"
              renderItem={(item) => (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <img
                      className="h-9 w-9 rounded-full object-cover ring-1 ring-gray-100"
                      src={item?.seeker?.photo?.thumb_url || defaultUserImage}
                      alt=""
                    />
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {item?.seeker?.first_name} {item?.seeker?.last_name}
                      </div>
                      <div className="text-xs font-medium text-primary-600">
                        {item?.position?.title || "-"}
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 flex items-center gap-1">
                    <i className="fas fa-map-marker-alt text-gray-300"></i>
                    {item?.location?.name || "-"}
                  </div>
                </div>
              )}
            />
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
